import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import TaskManager from './TaskManager';
import { getClerkConfig, loadClerkConfig } from '../utils/clerkConfigLoader';

// Error boundary component
class TaskManagerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('TaskManager Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          padding: '20px',
          textAlign: 'center'
        }}>
          <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>
            Task Manager Error
          </h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Something went wrong loading the Task Manager.
          </p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Reload Page
          </button>
          <details style={{ marginTop: '20px', maxWidth: '600px' }}>
            <summary style={{ cursor: 'pointer', color: '#666' }}>
              Error Details
            </summary>
            <pre style={{ 
              background: '#f3f4f6', 
              padding: '12px', 
              borderRadius: '4px', 
              fontSize: '12px',
              textAlign: 'left',
              overflow: 'auto'
            }}>
              {this.state.error?.toString()}
            </pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

// Component that safely handles useUser hook
const TaskManagerWithAuth = () => {
  try {
    const authState = useUser();
    return <TaskManager authState={authState} />;
  } catch (error) {
    console.error('Error in TaskManagerWithAuth:', error);
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Authentication Error</h3>
        <p>Unable to load user authentication. Please try refreshing the page.</p>
      </div>
    );
  }
};

// Component for when auth is disabled
const TaskManagerWithoutAuth = () => {
  const authState = { isSignedIn: true, user: null };
  return <TaskManager authState={authState} />;
};

const TaskManagerWrapper = () => {
  const [clerkConfig, setClerkConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeClerk = async () => {
      try {
        console.log('TaskManagerWrapper: Initializing Clerk...');
        
        // First check if config is already loaded
        let config = getClerkConfig();
        
        // If not loaded, load it
        if (!config) {
          console.log('TaskManagerWrapper: Loading Clerk config...');
          config = await loadClerkConfig();
        }
        
        console.log('TaskManagerWrapper: Clerk config loaded:', config);
        setClerkConfig(config);
      } catch (error) {
        console.error('Failed to load Clerk config in TaskManagerWrapper:', error);
        setError(error.message);
        setClerkConfig({ available: false, publishableKey: null });
      } finally {
        setLoading(false);
      }
    };

    initializeClerk();
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading Task Manager...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h3 style={{ color: '#dc2626', marginBottom: '16px' }}>
          Configuration Error
        </h3>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          {error}
        </p>
        <p style={{ color: '#666', fontSize: '14px' }}>
          The Task Manager will continue in local mode.
        </p>
        <button 
          onClick={() => {
            setError(null);
            setClerkConfig({ available: false, publishableKey: null });
          }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            marginTop: '12px'
          }}
        >
          Continue in Local Mode
        </button>
      </div>
    );
  }

  const isAuthEnabled = clerkConfig?.available && clerkConfig?.publishableKey;
  console.log('TaskManagerWrapper: Auth enabled:', isAuthEnabled);

  // Render appropriate component based on auth availability
  return (
    <TaskManagerErrorBoundary>
      {isAuthEnabled ? <TaskManagerWithAuth /> : <TaskManagerWithoutAuth />}
    </TaskManagerErrorBoundary>
  );
};

export default TaskManagerWrapper; 